import {
  ChangeDetectionStrategy,
  Component,
  createComponent,
  DOCUMENT,
  effect,
  EnvironmentInjector,
  inject,
  input,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import { GeoCoordinates } from '@components/store-locator/models';
import { environment } from '@environments/environment';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { DEFAULT_CENTER } from '@shared/components/map/config';
import { MapPopinComponent } from '@shared/components/map/map-popin';
import { MapConfig, OpportunityMarker, StoreMarker } from '@shared/components/map/models';
import { clustersRenderer } from '@shared/components/map/styles';
import { Store } from '@shared/models';
import { Opportunity } from '@shared/services/elastic-search/models';
import { GoogleService } from '@shared/services/google';
import { CurrentLanguageService } from '@shared/services/language';
import { LogsService } from '@shared/services/logs';
import { ResponsiveService } from '@shared/services/responsive';
import { StoreLocatorStore } from '@stores';
import { ElasticSearchService } from '@shared/services/elastic-search';
import { from, map, take } from 'rxjs';

@Component({
  selector: 'app-map',
  imports: [],
  templateUrl: './map.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapComponent implements OnInit {
  readonly config = input<MapConfig>();
  map!: google.maps.Map;
  readonly breakpoints = inject(ResponsiveService).breakpoints;
  private currentPopin: google.maps.OverlayView | null = null;
  private readonly defaultCenter = DEFAULT_CENTER[inject(CurrentLanguageService).language.region];
  private defaultZoom = 6;
  private readonly document = inject(DOCUMENT);
  private readonly googleService = inject(GoogleService);
  private readonly mapFeaturesStore = inject(StoreLocatorStore);
  private readonly window = inject(DOCUMENT).defaultView as Window;
  private readonly isMapLoaded = signal(false);
  private readonly injector = inject(EnvironmentInjector);
  private readonly logs = inject(LogsService);
  private readonly elasticSearchService = inject(ElasticSearchService);
  private storeClusterer: MarkerClusterer | null = null;
  private opportunityClusterer: MarkerClusterer | null = null;
  private storeMarkers: StoreMarker[] = [];
  private opportunityMarkers: OpportunityMarker[] = [];

  constructor() {
    effect(() => {
      const isMapViewForOpportunities = untracked(this.config)?.isMapViewForOpportunities;
      const isMapLoaded = this.isMapLoaded();
      const stores = this.mapFeaturesStore.select('stores')();
      if (isMapLoaded && stores) {
        this.logs.debug('MapComponent', 'stores', stores);
        from(google.maps.importLibrary('marker'))
          .pipe(
            take(1),
            map((markerLib) => {
              const { AdvancedMarkerElement } = markerLib;
              const markers: StoreMarker[] = stores.reduce<StoreMarker[]>((acc, loc) => {
                const img = document.createElement('img');
                img.src = 'assets/icons/pin-map-green.svg';
                img.width = img.height = 37;

                const lat = (loc.coordinates?.lat as number) ?? 0;
                const lng = (loc.coordinates?.lng as number) ?? 0;

                if (lat === 0 || lng === 0) {
                  this.logs.warn('MapComponent', 'The store does not have GPS coordinates', loc);
                  return acc;
                }

                const marker = new AdvancedMarkerElement({
                  position: { lat, lng },
                  content: img,
                  ...(loc.uuid ? { title: loc.name } : {}),
                }) as StoreMarker;

                if (!isMapViewForOpportunities && loc.uuid) {
                  marker.store = loc;
                  marker.addListener('click', () => {
                    this.zoomOnCoordinates(loc.coordinates, 0);
                    this.renderPopinForStore(loc);
                  });
                }

                acc.push(marker);
                return acc;
              }, []);

              this.storeMarkers = markers;

              if (!this.storeClusterer) {
                this.storeClusterer = new MarkerClusterer({
                  map: this.map,
                  algorithm: new SuperClusterAlgorithm({ radius: 130 }),
                  renderer: clustersRenderer(isMapViewForOpportunities ? 'ANIS' : 'PURPLE'),
                });
              }

              this.storeClusterer.clearMarkers();
              this.storeClusterer.addMarkers(this.storeMarkers);
              this.storeClusterer.render();

              this.logs.trace('MapComponent', 'markerCluster for stores', this.storeClusterer);
            })
          )
          .subscribe({
            error: (err) => this.logs.error('MapComponent', 'Loading marker library for stores failed', err),
          });
      }
    });
    effect(() => {
      const storeSelected = this.mapFeaturesStore.select('mapStoreSelected')();
      const onlyStoreForCurrentCountry = this.mapFeaturesStore.select('onlyStoreForCurrentCountry')();
      const isMapLoaded = untracked(this.isMapLoaded);
      if (storeSelected) {
        this.zoomOnCoordinates(
          {
            lat: storeSelected.coordinates.lat,
            lng: storeSelected.coordinates.lng,
          },
          10
        );
        this.renderPopinForStore(storeSelected);
      } else if (isMapLoaded) {
        if (this.currentPopin) {
          this.currentPopin.setMap(null);
          this.currentPopin = null;
        }
        if (onlyStoreForCurrentCountry) {
          this.zoomOnCoordinates(
            {
              lat: onlyStoreForCurrentCountry.coordinates.lat,
              lng: onlyStoreForCurrentCountry.coordinates.lng,
            },
            10
          );
          this.renderPopinForStore(onlyStoreForCurrentCountry);
        } else {
          this.map.panTo(this.defaultCenter);
          this.map.setZoom(this.defaultZoom);
        }
      }
    });
    effect(() => {
      const isMapLoaded = this.isMapLoaded();
      const opportunities = this.mapFeaturesStore.select('opportunities')();
      if (isMapLoaded && opportunities) {
        this.logs.debug('MapComponent', 'opportunities', opportunities);
        from(google.maps.importLibrary('marker'))
          .pipe(
            take(1),
            map((markerLib) => {
              const { AdvancedMarkerElement } = markerLib;
              const markers: OpportunityMarker[] = opportunities.reduce<OpportunityMarker[]>((acc, loc) => {
                const img = document.createElement('img');
                img.src = 'assets/icons/pin-map-purple.svg';
                img.width = img.height = 37;

                const lat = (loc.coordinates?.lat as number) ?? 0;
                const lng = (loc.coordinates?.lng as number) ?? 0;

                if (lat === 0 || lng === 0) {
                  this.logs.warn('MapComponent', 'The opportunity does not have GPS coordinates', loc);
                  return acc;
                }

                const marker = new AdvancedMarkerElement({
                  position: { lat, lng },
                  content: img,
                  ...(loc.tid ? { title: loc.name } : {}),
                }) as OpportunityMarker;

                if (loc.tid) {
                  marker.opportunity = loc;
                  marker.addListener('click', () => {
                    this.zoomOnCoordinates(loc.coordinates, 0);
                    this.renderPopinForStore(loc);
                  });
                }

                acc.push(marker);
                return acc;
              }, []);

              this.opportunityMarkers = markers;

              if (!this.opportunityClusterer) {
                this.opportunityClusterer = new MarkerClusterer({
                  map: this.map,
                  algorithm: new SuperClusterAlgorithm({ radius: 130 }),
                  renderer: clustersRenderer('PURPLE'),
                });
              }

              this.opportunityClusterer.clearMarkers();
              this.opportunityClusterer.addMarkers(this.opportunityMarkers);
              this.opportunityClusterer.render();

              this.logs.trace('MapComponent', 'markerCluster for opportunities', this.opportunityClusterer);
            })
          )
          .subscribe({
            error: (err) => this.logs.error('MapComponent', 'Loading marker library for opportunities failed', err),
          });
      }
    });
    effect(() => {
      const isMapLoaded = this.isMapLoaded();
      const config = this.config();
      if (!isMapLoaded || !config?.isMapViewForOpportunities) return;

      this.toggleLayer(this.opportunityClusterer, config.showOpeningOpportunities, this.opportunityMarkers);
      this.toggleLayer(this.storeClusterer, config.showOpenStores, this.storeMarkers);

      if (!config.showOpeningOpportunities || !config.showOpenStores) this.closePopin();
    });
  }

  ngOnInit(): void {
    this.defaultZoom = this.breakpoints() === 'mobile' ? 5 : 6;
    this.initMap();
  }

  private initMap(): void {
    this.googleService.waitForApiLoad().subscribe({
      next: () => {
        const mapContainer = this.document.getElementById('map-container') as HTMLElement;
        this.map = new google.maps.Map(mapContainer, {
          mapId: environment.integrations?.googleMapsApi?.mapId,
          disableDefaultUI: true,
          ...{
            center: this.defaultCenter,
            zoom: this.defaultZoom,
          },
        });
        this.isMapLoaded.set(true);
        const onlyStoreForCurrentCountry = this.mapFeaturesStore.select('onlyStoreForCurrentCountry')();
        if (onlyStoreForCurrentCountry) {
          this.zoomOnCoordinates({
            lat: onlyStoreForCurrentCountry.coordinates.lat,
            lng: onlyStoreForCurrentCountry.coordinates.lng,
          });
          this.renderPopinForStore(onlyStoreForCurrentCountry);
        }
        this.logs.trace('MapComponent', 'map', this.map);
      },
      error: (err) => this.logs.error('MapComponent', 'Google Maps API load failed, map not initialized', err),
    });
  }

  /**
   * Centers the map on specified coordinates and adjusts the zoom level.
   * @param coordinates The coordinates {lat, lng} on which to center the map.
   * @param zoomLevel The desired zoom level (defaults to 15).
   */
  private zoomOnCoordinates(coordinates: GeoCoordinates, zoomLevel: number = 15): void {
    if (this.map && coordinates) {
      this.map.panTo(coordinates as google.maps.LatLngLiteral);
      if (zoomLevel > 0) {
        this.map.setZoom(zoomLevel);
      }

      this.window.setTimeout(() => {
        this.map.panBy(0, 150); // ← déplace vers le haut de 150px
      }, 300);
    } else {
      this.logs.warn('MapComponent', 'Map is not initialized or coordinates are not provided for zoomOnCoordinates');
    }
  }

  private renderPopinForStore(marker: Store | Opportunity): void {
    if (this.currentPopin) {
      this.currentPopin.setMap(null);
      this.currentPopin = null;
    }

    const container = document.createElement('div');
    const viewRef = createComponent(MapPopinComponent, {
      environmentInjector: this.injector,
    });

    if ('uuid' in marker && marker?.uuid) {
      viewRef.instance.store = marker;
      this.elasticSearchService
        .getStoreSchedule(marker.uuid)
        .pipe(take(1))
        .subscribe({
          next: (schedule) => {
            viewRef.instance.operationalStatus.set(schedule.operationalStatus);
            viewRef.changeDetectorRef.detectChanges();
          },
          error: (err) => this.logs.error('MapComponent', 'getStoreSchedule failed for store popin', err),
        });
    }

    if ('tid' in marker && marker?.tid) {
      viewRef.instance.opportunity = marker;
    }

    viewRef.changeDetectorRef.detectChanges();
    container.appendChild(viewRef.location.nativeElement);

    const popup = new google.maps.OverlayView();

    popup.onAdd = function () {
      const panes = this.getPanes();
      if (panes) {
        panes.floatPane.appendChild(container);
      }
    };

    popup.draw = function () {
      const projection = this.getProjection();
      const pos = projection.fromLatLngToDivPixel(
        new google.maps.LatLng(marker.coordinates.lat as number, marker.coordinates.lng)
      );
      container.style.position = 'absolute';
      container.style.left = `${pos!.x}px`;
      container.style.top = `${pos!.y}px`;
    };

    popup.onRemove = function () {
      container.remove();
    };

    popup.setMap(this.map);

    this.currentPopin = popup;
  }

  private closePopin(): void {
    if (this.currentPopin) {
      this.currentPopin.setMap(null);
      this.currentPopin = null;
    }
  }

  private toggleLayer(
    clusterer: MarkerClusterer | null,
    show: boolean,
    source: google.maps.marker.AdvancedMarkerElement[]
  ): void {
    if (!clusterer) return;
    clusterer.clearMarkers();
    if (show) {
      clusterer.addMarkers(source);
      clusterer.render();
    }
  }
}
